import { useMemo } from 'react';

import type { CategoryModel, SubCategoryModel } from '@snowit/types/categories';

import { isNewEntity } from '@snowit/constants';
import useFirebase from '@snowit/firebase';
import { SchemaNames } from '@snowit/jsonschemas/__names';
import { useStore } from '@snowit/store';
import omitter from '@snowit/utilities/omitter';

import usePartners from '../usePartners';
import useSchemas from '../useSchemas';
import useTenants from '../useTenants';

export default () => {
  // const { user } = useAuth();
  const firebase = useFirebase();
  const { activeTenants, hasTenants } = useTenants();
  const { partner } = usePartners();
  const { store, setStore } = useStore();
  const { writeDocument, deleteDocument, deleteSubDocument, writeSubDocument } = useSchemas();

  const categories = useMemo(() => {
    return store.categories.list
      .filter((category) => {
        if (!activeTenants.length) return true;
        return hasTenants(category.tenants);
      })
      .filter((category) => {
        if (!partner) return true;
        return category.partnerUID === partner;
      });
  }, [store.categories.list, activeTenants, partner]);

  const getCategories = async (): Promise<CategoryModel[]> => {
    try {
      const res = await firebase.query<CategoryModel>({
        collectionName: 'categories',
        filters: (q) => q.limit(0),
      });
      setStore('categories', { list: res });
      return res;
    } catch (error) {
      console.error('categories_get', { error });
      return [];
    } finally {
      setStore('categories', { ready: true });
    }
  };

  const writeCategory = async (
    categoryUID: string | undefined,
    data: Partial<CategoryModel>,
  ): Promise<string | undefined> => {
    try {
      const id = await writeDocument({
        modelName: SchemaNames.CATEGORY,
        attributes: omitter(data),
        id: isNewEntity(categoryUID) ? undefined : categoryUID,
      });
      return id;
    } catch (error: any) {
      console.error('writeZone', { error });
    }
  };

  const getSubCategories = async (categoryUID: string): Promise<SubCategoryModel[]> => {
    return await firebase.subcollection<SubCategoryModel>({
      collectionName: 'categories',
      id: categoryUID,
      subcollectionName: 'subCategories',
    });
  };

  const writeSubCategory = async (
    categoryUID: string,
    subcategoryUID: string | undefined,
    data: Partial<SubCategoryModel>,
  ): Promise<string | undefined> => {
    try {
      if (isNewEntity(categoryUID)) return undefined;
      const id = await writeSubDocument({
        modelName: SchemaNames.CATEGORY_SUBCATEGORY,
        parentId: categoryUID,
        attributes: omitter(data),
        id: isNewEntity(subcategoryUID) ? undefined : subcategoryUID,
      });
      return id;
    } catch (error: any) {
      console.error('writeZone', { error });
    }
  };

  const deleteSubCategory = async (
    categoryUID: string,
    subcategoryUID: string,
  ): Promise<boolean> => {
    try {
      if (isNewEntity(subcategoryUID) || isNewEntity(categoryUID)) return false;
      await deleteSubDocument({
        modelName: SchemaNames.CATEGORY_SUBCATEGORY,
        parentId: categoryUID,
        id: subcategoryUID,
      });
      return true;
    } catch (error: any) {
      console.error('deleteCategory', { error });
      return false;
    }
  };

  const deleteCategory = async (categoryUID: string): Promise<boolean> => {
    try {
      if (isNewEntity(categoryUID)) return false;
      await deleteDocument({
        modelName: SchemaNames.CATEGORY,
        id: categoryUID,
      });
      return true;
    } catch (error: any) {
      console.error('deleteCategory', { error });
      return false;
    }
  };

  return {
    getCategories,
    categories,
    ready: store.categories.ready,
    writeCategory,
    getSubCategories,
    deleteCategory,
    deleteSubCategory,
    writeSubCategory,
  };
};
